import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { validateCPF, formatCPF, formatPhone } from "@/lib/validations";
import { Loader2 } from "lucide-react";

interface ConnectedUser {
  id: string;
  name: string;
  cpf: string;
  work_area: string;
  phone: string | null;
  email: string;
  permissions: string[];
}

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  editingUser: ConnectedUser | null;
  onSuccess: () => void;
}

const PERMISSIONS = [
  { value: "view", label: "Visualização de dados dos pacientes" },
  { value: "create", label: "Inclusão de novos pacientes" },
  { value: "update", label: "Alteração de dados dos pacientes" },
  { value: "delete", label: "Exclusão de pacientes" },
];

export function ConnectedUserFormModal({
  open,
  onOpenChange,
  editingUser,
  onSuccess,
}: Props) {
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    cpf: "",
    work_area: "",
    phone: "",
    email: "",
    permissions: [] as string[],
  });

  useEffect(() => {
    if (editingUser) {
      setFormData({
        name: editingUser.name,
        cpf: editingUser.cpf,
        work_area: editingUser.work_area,
        phone: editingUser.phone || "",
        email: editingUser.email,
        permissions: editingUser.permissions,
      });
    } else {
      setFormData({
        name: "",
        cpf: "",
        work_area: "",
        phone: "",
        email: "",
        permissions: [],
      });
    }
  }, [editingUser, open]);

  function togglePermission(permission: string) {
    setFormData((prev) => ({
      ...prev,
      permissions: prev.permissions.includes(permission)
        ? prev.permissions.filter((p) => p !== permission)
        : [...prev.permissions, permission],
    }));
  }

  async function handleSubmit() {
    if (!formData.name || !formData.cpf || !formData.email || !formData.work_area) {
      toast({
        variant: "destructive",
        title: "Campos obrigatórios",
        description: "Preencha todos os campos obrigatórios.",
      });
      return;
    }

    if (!validateCPF(formData.cpf)) {
      toast({
        variant: "destructive",
        title: "CPF inválido",
        description: "Por favor, informe um CPF válido.",
      });
      return;
    }

    if (formData.permissions.length === 0) {
      toast({
        variant: "destructive",
        title: "Permissões obrigatórias",
        description: "Selecione pelo menos uma permissão para o usuário.",
      });
      return;
    }

    try {
      setLoading(true);

      if (editingUser) {
        const { error: updateError } = await supabase.functions.invoke(
          "update-connected-user",
          {
            body: {
              userId: editingUser.id,
              ...formData,
            },
          }
        );

        if (updateError) throw updateError;

        toast({
          title: "Usuário atualizado",
          description: "O usuário foi atualizado com sucesso.",
        });
      } else {
        const { data, error: createError } = await supabase.functions.invoke(
          "create-connected-user",
          {
            body: formData,
          }
        );

        if (createError) throw createError;

        if (data?.temp_password) {
          toast({
            title: "Usuário criado com sucesso",
            description: `Senha temporária: ${data.temp_password}. Compartilhe com o usuário.`,
            duration: 10000,
          });
        } else {
          toast({
            title: "Usuário criado",
            description: "O usuário foi criado com sucesso.",
          });
        }
      }

      onSuccess();
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Erro ao salvar",
        description: error instanceof Error ? error.message : "Ocorreu um erro ao salvar o usuário.",
      });
    } finally {
      setLoading(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {editingUser ? "Editar Usuário Conectado" : "Novo Usuário Conectado"}
          </DialogTitle>
          <DialogDescription>
            {editingUser
              ? "Atualize os dados do usuário conectado"
              : "Adicione um novo usuário à sua equipe"}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2 space-y-2">
              <Label htmlFor="name">Nome Completo *</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) =>
                  setFormData({ ...formData, name: e.target.value })
                }
                placeholder="Digite o nome completo"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="cpf">CPF *</Label>
              <Input
                id="cpf"
                value={formData.cpf}
                onChange={(e) =>
                  setFormData({ ...formData, cpf: formatCPF(e.target.value) })
                }
                placeholder="000.000.000-00"
                maxLength={14}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="work_area">Área de Atuação *</Label>
              <Input
                id="work_area"
                value={formData.work_area}
                onChange={(e) =>
                  setFormData({ ...formData, work_area: e.target.value })
                }
                placeholder="Ex: Recepção, Enfermaria"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="phone">Telefone</Label>
              <Input
                id="phone"
                value={formData.phone}
                onChange={(e) =>
                  setFormData({ ...formData, phone: formatPhone(e.target.value) })
                }
                placeholder="(00) 00000-0000"
                maxLength={15}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="email">E-mail *</Label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) =>
                  setFormData({ ...formData, email: e.target.value })
                }
                placeholder="usuario@exemplo.com"
                disabled={!!editingUser}
              />
            </div>
          </div>

          <div className="space-y-3 pt-4 border-t">
            <Label>Permissões *</Label>
            <p className="text-sm text-muted-foreground mb-3">
              Selecione as permissões que o usuário terá sobre os dados dos pacientes:
            </p>
            {PERMISSIONS.map((permission) => (
              <div key={permission.value} className="flex items-center space-x-2">
                <Checkbox
                  id={permission.value}
                  checked={formData.permissions.includes(permission.value)}
                  onCheckedChange={() => togglePermission(permission.value)}
                />
                <Label
                  htmlFor={permission.value}
                  className="text-sm font-normal cursor-pointer"
                >
                  {permission.label}
                </Label>
              </div>
            ))}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancelar
          </Button>
          <Button onClick={handleSubmit} disabled={loading}>
            {loading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            {editingUser ? "Atualizar" : "Criar e Vincular"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
